import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./ServicePage.css";
import ImageCarousel from "./ImageCarousel";

/* ---------- tiny hook: reveal on scroll ---------- */
function useReveal() {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current || inView) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [inView]);

  return [ref, inView];
}

/* ---------- helpers ---------- */
const Accent = ({ children }) => <span className="accent">{children}</span>;

const Accentify = ({ text, accent }) => {
  if (!accent || !text?.includes?.(accent)) return text;
  const [before, after] = text.split(accent);
  return (
    <>
      {before}
      <Accent>{accent}</Accent>
      {after}
    </>
  );
};

/** Small i18n helper that reads from default ns at "services.<slug>..." */
function useSvcTxt(slug) {
  const { t } = useTranslation(); // default namespace (your JSON)
  const base = React.useMemo(() => `services.${slug}`, [slug]);

  /** read a key under base, with optional defaultValue */
  const read = React.useCallback(
    (path, fallback, opts = {}) =>
      t(`${base}.${path}`, { defaultValue: fallback ?? "", ...opts }),
    [t, base]
  );

  /** read an object/array (e.g., blocks.N.copy as { "0": "...", "1": "..." }) */
  const readObj = React.useCallback(
    (path, fallback) =>
      t(`${base}.${path}`, { defaultValue: fallback ?? "", returnObjects: true }),
    [t, base]
  );

  return { read, readObj };
}

/* ---------- sections ---------- */
const Hero = ({ slug, hero }) => {
  const { read } = useSvcTxt(slug);

  return (
    <section
      className="sp-hero"
      style={{
        backgroundImage: hero.bgVideo || hero.bgVideoSources ? "none" : `url(${hero.bgImage})`,
        backgroundPosition: hero.imagePosition || "center",
        backgroundSize: hero.bgSize || "cover",
      }}
      aria-label="Service hero"
    >
      {(hero.bgVideoSources || hero.bgVideo) && (
        <video
          className="sp-hero__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={hero.bgImage}
        >
          {hero.bgVideoSources ? (
            hero.bgVideoSources.map((source, idx) => (
              <source key={idx} src={source.src} type={source.type} />
            ))
          ) : (
            <source src={hero.bgVideo} type="video/mp4" />
          )}
          Your browser does not support the video tag.
        </video>
      )}

      <div className="sp-hero__overlay">
        <div className="sp-hero__panel">
          <h1 className="sp-hero__title">
            {hero.titleLines?.map((line, i) => (
              <span key={i} className="sp-hero__line">
                <Accentify
                  text={read(`hero.title${i}`, line.text)}
                  accent={line.accent}
                />
              </span>
            ))}
          </h1>

          {hero.kicker && (
            <p className="sp-hero__kicker">{read("hero.kicker", hero.kicker)}</p>
          )}

          {hero.blurb && (
            <p className="sp-hero__blurb">{read("hero.blurb", hero.blurb)}</p>
          )}

          <a className="sp-btn" href={hero.cta?.href || "/contact"}>
            {read("hero.cta", hero.cta?.label || "Talk to an Expert")}
          </a>
        </div>
      </div>
    </section>
  );
};

const EyebrowBlock = ({ slug, node }) => {
  const { read } = useSvcTxt(slug);
  const [ref, inView] = useReveal();

  return (
    <section ref={ref} className={`eyebrow-block reveal ${inView ? "in" : ""}`}>
      {node.eyebrow && <div className="eyebrow">{read("expertise.eyebrow", node.eyebrow)}</div>}
      {node.title && (
        <h2 className="mega reveal-item" style={{ transitionDelay: "120ms" }}>
          <Accentify text={read("expertise.title", node.title)} accent={node.accent} />
        </h2>
      )}
      {node.copy && (
        <p className="lead reveal-item" style={{ transitionDelay: "220ms" }}>
          {read("expertise.copy", node.copy)}
        </p>
      )}
    </section>
  );
};

const SplitBlock = ({ slug, index, block, flip }) => {
  const { read, readObj } = useSvcTxt(slug);

  const [mediaRef, mediaIn] = useReveal();
  const [contentRef, contentIn] = useReveal();

  const mediaDir = flip ? "reveal--right" : "reveal--left";
  const contentDir = flip ? "reveal--left" : "reveal--right";

  const renderCopy = () => {
    // Try to read object/array first
    const tVal = readObj(`blocks.${index}.copy`, block.copy);

    // If translator returned a string, show it
    if (typeof tVal === "string") {
      return <p>{tVal}</p>;
    }

    // If it's an object (e.g., { "0": "...", "1": "..." }) map in numeric order
    if (tVal && typeof tVal === "object") {
      const keys = Object.keys(tVal).sort((a, b) => Number(a) - Number(b));
      return keys.map((k, i) => (
        <p key={i} style={{ marginBottom: i < keys.length - 1 ? "1rem" : 0 }}>
          {tVal[k]}
        </p>
      ));
    }

    // Fallback: original data (string or array)
    if (Array.isArray(block.copy)) {
      return block.copy.map((p, i) => (
        <p key={i} style={{ marginBottom: i < block.copy.length - 1 ? "1rem" : 0 }}>
          {p}
        </p>
      ));
    }
    return block.copy ? <p>{block.copy}</p> : null;
  };

  return (
    <section className={`split ${flip ? "split--flip" : ""}`}>
      <div
        ref={mediaRef}
        className={`split__media reveal ${mediaDir} ${mediaIn ? "in" : ""}`}
        style={{ transitionDelay: "60ms" }}
      >
        {(block.videoSources || block.video || block.image) &&
          (block.videoSources || block.video || (typeof block.image === "string" && (block.image.endsWith(".mp4") || block.image.endsWith(".webm"))) ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            >
              {block.videoSources ? (
                block.videoSources.map((source, idx) => (
                  <source key={idx} src={source.src} type={source.type} />
                ))
              ) : (
                <source src={block.video || block.image} type={block.video?.endsWith(".webm") || block.image?.endsWith(".webm") ? "video/webm" : "video/mp4"} />
              )}
              Your browser does not support the video tag.
            </video>
          ) : (
            <img src={block.image} alt={block.imageAlt || ""} loading="lazy" />
          ))}
      </div>

      <div
        ref={contentRef}
        className={`split__content reveal ${contentDir} ${contentIn ? "in" : ""}`}
      >
        {Array.isArray(block.icons) && (
          <div className="split__icons reveal-item" style={{ transitionDelay: "60ms" }}>
            {block.icons.map((icon, i) => (
              <span key={i} className="split__icon">
                {icon}
              </span>
            ))}
          </div>
        )}

        {block.title && (
          <h3 className="split__title reveal-item" style={{ transitionDelay: "120ms" }}>
            <Accentify text={read(`blocks.${index}.title`, block.title)} accent={block.accent} />
          </h3>
        )}

        {block.copy && (
          <div className="split__copy reveal-item" style={{ transitionDelay: "220ms" }}>
            {renderCopy()}
          </div>
        )}

        {Array.isArray(block.points) && (
          <ul className="split__list">
            {block.points.map((p, i) => (
              <li
                key={i}
                className="reveal-item"
                style={{ transitionDelay: `${260 + i * 80}ms` }}
              >
                {p}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

function FooterCTA({ slug, node }) {
  const { read } = useSvcTxt(slug);
  const f = node.footerCta || {};
  return (
    <section className="final-cta">
      <h3>
        <Accentify text={read("footerCta.title", f.title)} accent={f.accent} />
      </h3>
      <p>{read("footerCta.copy", f.copy)}</p>
      <a className="service-btn" href={f.href || "/contact"}>
        {read("footerCta.label", f.label)}
      </a>
    </section>
  );
}

export default function ServicePage({ data }) {
  if (!data) return null;
  const slug = data.slug; // REQUIRED and must match your JSON keys under "services"

  return (
    <main className="service-page">
      {data.hero && <Hero slug={slug} hero={data.hero} />}

      {data.expertise && <EyebrowBlock slug={slug} node={data.expertise} />}

      {data.carouselImages && data.carouselImages.length > 0 && (
        <ImageCarousel images={data.carouselImages} />
      )}

      {Array.isArray(data.blocks) &&
        data.blocks.map((b, i) => (
          <SplitBlock
            key={i}
            slug={slug}
            index={i + 1}           // 1-based to match your JSON: "blocks": { "1": { ... } }
            block={b}
            flip={b.flip ?? i % 2 === 1}
          />
        ))}

      {data.footerCta && <FooterCTA slug={slug} node={data} />}
    </main>
  );
}

ServicePage.propTypes = {
  data: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    hero: PropTypes.object,
    expertise: PropTypes.object,
    blocks: PropTypes.arrayOf(PropTypes.object),
    footerCta: PropTypes.object,
  }),
};
